import {useCallback, useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {ChngXmMode, MyDashboard} from '../../../redux/Slice/studentSlice'
import SocialMedia from 'app/modules/student/SocialMedia'
import StdAccountPayment from './StdAccountPayment'
import i18n from 'i18n'
import EmergencyContact from './EmergencyContact'
import {useAuth} from 'app/modules/auth'
import InactiveUser from './InactiveUser'
import StdMenu from 'app/modules/student/StdMenu'
import {toAbsoluteUrl} from '_metronic/helpers'
import {Modal} from 'app/custom/modal/Modal'
import StudentDocumentCreate from 'app/modules/admin/student/student/StudentDocumentCreate'
import {encryptId} from '_metronic/helpers/Encrypt'
import {Link} from 'react-router-dom'
import {showConfirmation, showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'

const StudentDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [news, setNews] = useState<any[]>([])
  const [planning, setPlanning] = useState<any>(null)
  const [openSemester, setOpenSemester] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)
  const [openNews, setOpenNews] = useState<number[]>([])
  const [isActive, setIsActive] = useState(false)
  const [isShown, setIsShown] = useState(false)
  const toggle = useCallback(() => setIsShown((prev) => !prev), [])
  const [isShownDoc, setIsShownDoc] = useState(false)
  const toggleDoc = useCallback(() => setIsShownDoc((prev) => !prev), [])
  const {currentUser} = useAuth()
  const [studentId, setStudentId] = useState<any>('')
  const [examDate, setExamDate] = useState<any>('')
  const [xmId, setXmId] = useState<any>('')
  const [hasDocument, setHasDocument] = useState(true)
  const [examResult, setExamResult] = useState<any>(null)
  const [memberExams, setMemberExams] = useState<any[]>([])

  const listData = () => {
    dispatch(MyDashboard()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const resp = res.payload
        setNews(resp.news)
        setExamDate(resp.kankor_exam_date)
        setPlanning(resp.planning)
        setXmId(resp.kankor_exam_id)
        setExamResult(resp.entrance_exam_result)
        if (resp.has_emergency_contact === false) toggle()
        if (resp.has_document === false) toggleDoc()
        setHasDocument(resp.has_document)
        setStudentId(resp.student_id)
        setMemberExams(resp.member_exams ?? [])
        setLoader(false)
      }
    })
  }

  useEffect(() => {
    if (currentUser?.status === 'Inactive') setIsActive(true)
    listData()
  }, [])

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const [datePart, timePart] = dateString.split(' ')
    const [day, month, year] = datePart.split('-')

    const formatted = `${year}-${month}-${day}T${timePart}`

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }

    return new Date(formatted).toLocaleString('en-GB', options)
  }

  function toSortableDate(dateStr: string): string {
    const [day, month, year] = dateStr.split('-')
    return `${year}-${month}-${day}`
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [news, itemsPerPage])

  if (loader) return <Loader />

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = news.slice(indexOfFirstItem, indexOfLastItem)

  const EntranceExamResultCard = ({result}: {result: any}) => {
    if (!result) return null

    const isPending = result.status === 'Pending'
    const isApproved = result.status === 'Approved'
    const isPassed = isApproved && result.is_passed === true
    const isFailed = isApproved && result.is_passed === false

    const chance = Number(result.chance)

    const isWithinExamWindow = (() => {
      if (!examDate) return false

      const dateOnly = examDate.split(' ')[0]
      const [day, month, year] = dateOnly.split('-').map(Number)

      const exam = new Date(year, month - 1, day)
      const today = new Date()

      exam.setHours(0, 0, 0, 0)
      today.setHours(0, 0, 0, 0)

      const examEndDate = new Date(exam)
      examEndDate.setDate(examEndDate.getDate() + 30)
      examEndDate.setHours(0, 0, 0, 0)

      return today.getTime() >= exam.getTime() && today.getTime() < examEndDate.getTime()
    })()

    const isSecondChance = isFailed && chance === 1
    const canTakeSecondChance = isSecondChance && isWithinExamWindow
    const isFinalFailure =
      (isFailed && chance === 2) || (isFailed && chance === 1 && !isWithinExamWindow)

    // CARD DESIGN
    let background = ''
    let icon = ''
    let title = ''
    let description = ''

    // PENDING
    if (isPending) {
      background = 'linear-gradient(135deg, #3730a3 0%, #4f46e5 55%, #6366f1 100%)'
      icon = 'fas fa-hourglass-half'
      title = t('EX_RESULT.RESULT_PENDING')
      description = t('EX_RESULT.RESULT_PENDING_DESC')
    }

    // PASSED
    if (isPassed) {
      background = 'linear-gradient(135deg, #047857 0%, #059669 55%, #10b981 100%)'
      icon = 'fas fa-check-circle'
      title = t('EX_RESULT.EXAM_PASSED')
      description = t('EX_RESULT.EXAM_PASSED_DESC')
    }

    // FAILED
    if (isFailed) {
      if (chance === 1 && isWithinExamWindow) {
        background = 'linear-gradient(135deg, #92400e 0%, #d97706 52%, #f59e0b 100%)'
        icon = 'fas fa-redo-alt'
        title = t('EX_RESULT.SEC_CHANCE')
        description = t('EX_RESULT.SEC_CHANCE_TODAY')
      }

      if (chance === 1 && !isWithinExamWindow) {
        background = 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 55%, #ef4444 100%)'
        icon = 'fas fa-info-circle'
        title = t('EX_RESULT.FAILED_MSG')
        description = t('EX_RESULT.FAILED_MSG_DESC')
      }

      if (chance === 2) {
        background = 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 55%, #ef4444 100%)'
        icon = 'fas fa-info-circle'
        title = t('EX_RESULT.FAILED_MSG')
        description = t('EX_RESULT.FAILED_MSG_DESC')
      }
    }

    return (
      <>
        <style>
          {`
          @keyframes compactExamEnter {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes compactExamPulse {
            0%, 100% {
              transform: scale(1);
            }

            50% {
              transform: scale(1.08);
            }
          }

          .compact-exam-result {
            animation: compactExamEnter .5s ease;
          }

          .compact-exam-icon {
            animation: compactExamPulse 2.2s ease-in-out infinite;
          }

          .compact-result-btn {
            transition:
              transform .2s ease,
              box-shadow .2s ease,
              background .2s ease;
          }

          .compact-result-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 7px 18px rgba(0,0,0,.16);
          }

          .exam-score-box {
            transition:
              transform .2s ease,
              background .2s ease;
          }

          .exam-score-box:hover {
            transform: translateY(-2px);
            background: rgba(255,255,255,.20) !important;
          }
        `}
        </style>

        <div className='mx-3 mb-4'>
          <div
            className='compact-exam-result position-relative overflow-hidden rounded-4'
            style={{
              background,
              boxShadow: '0 10px 28px rgba(0,0,0,.10)',
            }}
          >
            {/* DECORATIVE CIRCLE */}
            <div
              className='position-absolute rounded-circle'
              style={{
                width: '130px',
                height: '130px',
                top: '-70px',
                right: '10%',
                background: 'rgba(255,255,255,.07)',
              }}
            />

            <div
              className='position-absolute rounded-circle'
              style={{
                width: '90px',
                height: '90px',
                bottom: '-50px',
                left: '25%',
                background: 'rgba(255,255,255,.05)',
              }}
            />

            <div className='position-relative px-5 py-4'>
              <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4'>
                {/* LEFT SIDE */}
                <div className='d-flex align-items-start gap-4 flex-grow-1'>
                  <div
                    className='compact-exam-icon d-flex align-items-center justify-content-center rounded-3 flex-shrink-0'
                    style={{
                      width: '54px',
                      height: '54px',
                      background: 'rgba(255,255,255,.16)',
                      border: '1px solid rgba(255,255,255,.22)',
                    }}
                  >
                    <span
                      className={`${icon} text-white`}
                      style={{
                        fontSize: '23px',
                      }}
                    />
                  </div>

                  <div className='flex-grow-1'>
                    {/* STATUS BADGE */}
                    <div className='d-flex align-items-center flex-wrap gap-2 mb-1'>
                      {/* PENDING */}
                      {isPending && (
                        <span
                          className='badge rounded-pill px-3 py-2'
                          style={{
                            color: '#fff',
                            background: 'rgba(255,255,255,.15)',
                          }}
                        >
                          <span className='fas fa-circle-notch fa-spin mx-1' />
                          {t('EX_RESULT.RESULT_PENDING')}
                        </span>
                      )}

                      {/* PASSED */}
                      {isPassed && (
                        <span
                          className='badge rounded-pill px-3 py-2'
                          style={{
                            color: '#fff',
                            background: 'rgba(255,255,255,.15)',
                          }}
                        >
                          <span className='fas fa-check-circle mx-1' />
                          {t('EX_RESULT.PASSED')}
                        </span>
                      )}

                      {/* SECOND CHANCE */}
                      {canTakeSecondChance && (
                        <span
                          className='badge rounded-pill px-3 py-2'
                          style={{
                            color: '#fff',
                            background: 'rgba(255,255,255,.15)',
                          }}
                        >
                          <span className='fas fa-redo-alt mx-1' />
                          {t('EX_RESULT.SEC_CHANCE_')}
                        </span>
                      )}

                      {/* FINAL FAILURE */}
                      {isFinalFailure && (
                        <span
                          className='badge rounded-pill px-3 py-2'
                          style={{
                            color: '#fff',
                            background: 'rgba(255,255,255,.15)',
                          }}
                        >
                          <span className='fas fa-info-circle mx-1' />
                          {t('EX_RESULT.FINAL_RESULT')}
                        </span>
                      )}
                    </div>
                    {/* TITLE */}
                    <h4 className='text-white fw-bolder mb-1'>{title}</h4>
                    {/* DESCRIPTION */}
                    <div
                      className='fs-7'
                      style={{
                        color: 'rgba(255,255,255,.82)',
                        maxWidth: '780px',
                        lineHeight: '1.6',
                      }}
                    >
                      {description}
                    </div>

                    {isApproved && (
                      <div className='d-flex align-items-center flex-wrap gap-2 mt-3'>
                        <div
                          className='exam-score-box d-flex align-items-center gap-2 rounded-3 px-3 py-2'
                          style={{
                            background: 'rgba(255,255,255,.14)',
                            border: '1px solid rgba(255,255,255,.18)',
                          }}
                        >
                          <span className='fas fa-star text-white' />

                          <span
                            className='fw-semibold'
                            style={{
                              color: 'rgba(255,255,255,.72)',
                            }}
                          >
                            {t('EX_RESULT.SCORE')}
                          </span>
                          <span className='text-white fw-bolder fs-6'>
                            {result.earned_score} / {result.total_score}
                          </span>
                        </div>

                        {/* REQUIRED SCORE */}
                        <div
                          className='exam-score-box d-flex align-items-center gap-2 rounded-3 px-3 py-2'
                          style={{
                            background: 'rgba(255,255,255,.14)',
                            border: '1px solid rgba(255,255,255,.18)',
                          }}
                        >
                          <span className='fas fa-bullseye text-white' />

                          <span
                            className='fw-semibold'
                            style={{
                              color: 'rgba(255,255,255,.72)',
                            }}
                          >
                            {t('EX_RESULT.REQUIRED_SCORE')}
                          </span>
                          <span className='text-white fw-bolder fs-6'>{result.success_score}</span>
                        </div>

                        {/* RESULT */}
                        <div
                          className='exam-score-box d-flex align-items-center gap-2 rounded-3 px-3 py-2'
                          style={{
                            background: isPassed ? 'rgba(6,78,59,.25)' : 'rgba(127,29,29,.25)',
                            border: '1px solid rgba(255,255,255,.18)',
                          }}
                        >
                          <span
                            className={`fas ${
                              isPassed ? 'fa-check-circle' : 'fa-times-circle'
                            } text-white`}
                          />

                          <span
                            className='fw-semibold'
                            style={{
                              color: 'rgba(255,255,255,.72)',
                            }}
                          >
                            {t('EX_RESULT.REQUIRED_SCORE')}
                          </span>

                          <span className='text-white fw-bolder'>
                            {isPassed ? t('EX_RESULT.PASSED') : t('EX_RESULT.FAILED')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT SIDE */}
                <div className='d-flex align-items-center flex-wrap gap-2 flex-shrink-0'>
                  {/* PENDING */}
                  {isPending && (
                    <div
                      className='d-flex align-items-center gap-2 px-4 py-3 rounded-3 fw-semibold'
                      style={{
                        background: 'rgba(255,255,255,.12)',
                        color: 'rgba(255,255,255,.9)',
                        border: '1px solid rgba(255,255,255,.15)',
                      }}
                    >
                      <span className='fas fa-shield-alt' />
                      {t('EX_RESULT.VERIFICATION')}
                    </div>
                  )}

                  {/* PASSED */}
                  {isPassed && (
                    <Link
                      to={`/student/entrance-exam-result/${result.id}`}
                      className='compact-result-btn btn border-0 fw-bolder rounded-3 px-5 py-3'
                      style={{
                        background: '#fff',
                        color: '#047857',
                        minWidth: '150px',
                      }}
                    >
                      <span className='fas fa-eye mx-2' />
                      {t('EX_RESULT.VIEW_RESULT')}
                    </Link>
                  )}

                  {/* SECOND CHANCE */}
                  {canTakeSecondChance && (
                    <>
                      <Link
                        to={`/student/entrance-exam/${encryptId(xmId)}`}
                        className='compact-result-btn btn border-0 fw-bolder rounded-3 px-5 py-3'
                        style={{
                          background: '#fff',
                          color: '#b45309',
                          minWidth: '180px',
                        }}
                      >
                        <span className='fas fa-play-circle mx-2' />
                        {t('ONLINE.START_EXAM')}
                      </Link>

                      <Link
                        to={`/student/entrance-exam-result/${result.id}`}
                        className='btn fw-bolder rounded-3 px-4 py-3'
                        style={{
                          background: 'rgba(255,255,255,.13)',
                          border: '1px solid rgba(255,255,255,.25)',
                          color: '#fff',
                        }}
                      >
                        <span className='fas fa-eye mx-2' />
                        {t('EX_RESULT.VIEW_RESULT')}
                      </Link>
                    </>
                  )}

                  {/* FINAL FAILURE */}
                  {isFinalFailure && (
                    <Link
                      to={`/student/entrance-exam-result/${result.id}`}
                      className='compact-result-btn btn border-0 fw-bolder rounded-3 px-5 py-3'
                      style={{
                        background: '#fff',
                        color: '#b91c1c',
                        minWidth: '150px',
                      }}
                    >
                      <span className='fas fa-eye mx-2' />
                      {t('EX_RESULT.VIEW_RESULT')}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const handleCampusExamClick = () => {
    showConfirmation(
      t('MESSAGES.XM_CONFIRMATION'),
      t('GLOBAL.YES'),
      t('GLOBAL.NO'),
      () => {
        setLoader(true)
        dispatch(ChngXmMode()).then((res) => {
          if (res.meta.requestStatus === 'fulfilled') {
            if (
              res.payload.message === 'Please complete payments before proceeding.' ||
              res.payload.message === 'Credits Not Completed Yet'
            ) {
              showErrorMessage(res.payload.message)
              setLoader(false)
            } else {
              showSuccessMessage(t('MESSAGES.UPDATED'), () => {
                listData()
              })
            }
          } else showErrorMessage(t('MESSAGES.SERVER_ERROR'))
        })
      },
      () => showErrorMessage(t('MESSAGES.SERVER_ERROR'))
    )
  }

  return (
    <>
      <StdMenu />
      <div className='banner-container mb-4 shadow-sm'>
        <img
          src={toAbsoluteUrl('/media/bg/student_homepage_bg.png')}
          alt='welcome'
          className='banner-image'
        />

        <div className='banner-content'>
          <div className='banner-icon'>
            <i className='fas fa-graduation-cap'></i>
            <span className='banner-star'>
              <i className='fas fa-star'></i>
            </span>
          </div>

          <div>
            <h1 className='banner-title'>{t('ONLINE.WELCOME') + ' ' + currentUser?.username}</h1>
            <p className='banner-subtitle'>{t('ONLINE.STD_WEL_MSG')}</p>
          </div>
        </div>
      </div>

      {examResult && <EntranceExamResultCard result={examResult} />}

      {examDate &&
        !examResult &&
        (() => {
          const dateOnly = examDate.split(' ')[0]
          const [day, month, year] = dateOnly.split('-').map(Number)
          const exam = new Date(year, month - 1, day)
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          exam.setHours(0, 0, 0, 0)

          const formattedExamDate = `${String(day).padStart(2, '0')}-${String(month).padStart(
            2,
            '0'
          )}-${year}`

          // Student has 30 days starting from the exam date
          const examEndDate = new Date(exam)
          examEndDate.setDate(examEndDate.getDate() + 30)
          examEndDate.setHours(0, 0, 0, 0)

          const isToday =
            today.getTime() >= exam.getTime() && today.getTime() < examEndDate.getTime()
          const isFuture = today.getTime() < exam.getTime()
          const isPassed = today.getTime() >= examEndDate.getTime()
          if (isToday) {
            return (
              <div className='mb-4 mx-3'>
                <div
                  className='position-relative overflow-hidden rounded-4 mb-4'
                  style={{
                    background: 'linear-gradient(135deg, #0f766e 0%, #16a34a 55%, #22c55e 100%)',
                    boxShadow: '0 12px 35px rgba(22, 163, 74, 0.22)',
                  }}
                >
                  <div
                    className='position-absolute rounded-circle'
                    style={{
                      width: '180px',
                      height: '180px',
                      background: 'rgba(255,255,255,0.08)',
                      top: '-90px',
                      right: '-40px',
                    }}
                  />
                  <div
                    className='position-absolute rounded-circle'
                    style={{
                      width: '100px',
                      height: '100px',
                      background: 'rgba(255,255,255,0.06)',
                      bottom: '-55px',
                      left: '30%',
                    }}
                  />

                  <div className='position-relative p-6 p-md-8'>
                    <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-5'>
                      <div className='d-flex align-items-center gap-4'>
                        <div
                          className='d-flex align-items-center justify-content-center rounded-4'
                          style={{
                            width: '68px',
                            height: '68px',
                            minWidth: '68px',
                            background: 'rgba(255,255,255,0.16)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.22)',
                          }}
                        >
                          <i className='fas fa-graduation-cap text-white fs-1'></i>
                        </div>

                        <div>
                          <div className='d-flex align-items-center flex-wrap gap-2 mb-2'>
                            <span
                              className='badge rounded-pill px-3 py-2'
                              style={{
                                background: 'rgba(255,255,255,0.18)',
                                color: '#fff',
                                border: '1px solid rgba(255,255,255,0.22)',
                              }}
                            >
                              <span
                                className='d-inline-block rounded-circle mx-2'
                                style={{
                                  width: '7px',
                                  height: '7px',
                                  background: '#fff',
                                  boxShadow: '0 0 0 4px rgba(255,255,255,.18)',
                                }}
                              ></span>

                              {i18n.language === 'fr' ? "AUJOURD'HUI" : 'TODAY'}
                            </span>
                          </div>

                          <h2 className='text-white fw-bolder mb-1'>
                            {t('ONLINE.ENTRANCE_EXAMINATION')}
                          </h2>

                          <div className='text-white opacity-75 fs-6'>
                            {i18n.language === 'fr'
                              ? "Votre examen d'entrée est maintenant disponible."
                              : 'Your entrance examination is now available.'}
                          </div>
                        </div>
                      </div>

                      {/* EXAM DATE */}
                      <div
                        className='rounded-4 px-5 py-3 text-center'
                        style={{
                          background: 'rgba(255,255,255,0.14)',
                          border: '1px solid rgba(255,255,255,0.22)',
                          backdropFilter: 'blur(10px)',
                          minWidth: '155px',
                        }}
                      >
                        <div className='text-white opacity-75 fs-8 fw-bold text-uppercase mb-1'>
                          {i18n.language === 'fr' ? "DATE D'EXAMEN" : 'EXAM DATE'}
                        </div>

                        <div className='text-white fw-bolder fs-3'>{formattedExamDate}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className='rounded-4 border bg-white overflow-hidden'
                  style={{
                    boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
                  }}
                >
                  <div className='p-6'>
                    <div className='alert bg-light-primary border fw-bold'>
                      {i18n.language === 'en' ? (
                        <>
                          To ensure fairness, assessment quality and academic integrity, this online
                          examination may be monitored and/or recorded.
                        </>
                      ) : (
                        <>
                          Afin de garantir l’équité, la qualité de l’évaluation et l’intégrité
                          académique, cet examen en ligne peut faire l’objet d’une surveillance
                          et/ou d’un enregistrement.
                        </>
                      )}
                    </div>

                    <ul className='list-group list-group-flush mb-3 fs-6'>
                      <li className='list-group-item'>
                        <b>
                          ✅{i18n.language === 'en' ? <> Webcam Enabled</> : <> Caméra activée</>}
                        </b>
                        <br />
                        <p className='mx-5 mb-1'>
                          {i18n.language === 'en' ? (
                            <>
                              I understand that my webcam must remain enabled throughout the
                              examination, where required.
                            </>
                          ) : (
                            <>
                              Je comprends que ma caméra doit rester activée pendant toute la durée
                              de l’examen, lorsque cela est requis.
                            </>
                          )}
                        </p>
                      </li>

                      <li className='list-group-item'>
                        <b>
                          🎥
                          {i18n.language === 'en' ? (
                            <> Session Monitored and/or Recorded</>
                          ) : (
                            <> Session surveillée et/ou enregistrée</>
                          )}
                        </b>
                        <br />
                        <p className='mx-5 mb-1'>
                          {i18n.language === 'en' ? (
                            <>
                              I understand that my webcam, microphone and/or screen may be monitored
                              or recorded solely for identity verification, examination security,
                              fraud prevention and incident review purposes.
                            </>
                          ) : (
                            <>
                              Je comprends que ma caméra, mon microphone et/ou mon écran peuvent
                              être surveillés ou enregistrés uniquement à des fins de vérification
                              d’identité, de sécurité de l’examen, de prévention de la fraude et
                              d’analyse d’incidents.
                            </>
                          )}
                        </p>
                      </li>

                      <li className='list-group-item'>
                        <b>
                          📋
                          {i18n.language === 'en' ? <> Examination Rules</> : <> Règles d’examen</>}
                        </b>
                        <br />
                        <p className='mx-5 mb-1'>
                          {i18n.language === 'en' ? (
                            <>
                              I confirm that I have read and understood the Online Examination
                              Regulations and the Data Protection Notice.
                            </>
                          ) : (
                            <>
                              Je confirme avoir pris connaissance du Règlement des examens en ligne
                              et de la Notice de protection des données.
                            </>
                          )}
                        </p>
                      </li>

                      <li className='list-group-item'>
                        <b>
                          👤
                          {i18n.language === 'en' ? (
                            <> Visible Within the Camera Frame</>
                          ) : (
                            <> Présence dans le cadre</>
                          )}
                        </b>
                        <br />
                        <p className='mx-5 mb-1'>
                          {i18n.language === 'en' ? (
                            <>
                              I agree to remain visible within the camera frame throughout the
                              examination.
                            </>
                          ) : (
                            <>
                              Je m’engage à rester visible dans le cadre de la caméra pendant toute
                              la durée de l’examen.
                            </>
                          )}
                        </p>
                      </li>

                      <li className='list-group-item'>
                        <b>
                          🚫
                          {i18n.language === 'en' ? (
                            <> No Unauthorised Tools</>
                          ) : (
                            <> Aucun outil non autorisé</>
                          )}
                        </b>
                        <br />
                        <p className='mx-5 mb-1'>
                          {i18n.language === 'en' ? (
                            <>
                              I confirm that I am alone and will not use any unauthorised device,
                              document, website, software, communication tool or AI tool.
                            </>
                          ) : (
                            <>
                              Je confirme être seul·e et m’engage à ne pas utiliser d’appareil, de
                              document, de site internet, de logiciel, d’outil de communication ou
                              d’outil d’intelligence artificielle non autorisé.
                            </>
                          )}
                        </p>
                      </li>

                      <li className='list-group-item'>
                        <b>
                          🔐
                          {i18n.language === 'en' ? (
                            <> Data Protection and Human Review</>
                          ) : (
                            <> Protection des données et contrôle humain</>
                          )}
                        </b>
                        <br />
                        <p className='mx-5 mb-1'>
                          {' '}
                          {i18n.language === 'en' ? (
                            <>
                              I understand that recordings and technical logs are kept for a limited
                              period, accessed only by authorised staff, and that no academic or
                              disciplinary decision will be taken solely on the basis of an
                              automated alert.
                            </>
                          ) : (
                            <>
                              Je comprends que les enregistrements et journaux techniques sont
                              conservés pour une durée limitée, accessibles uniquement au personnel
                              autorisé, et qu’aucune décision académique ou disciplinaire ne sera
                              prise uniquement sur la base d’une alerte automatique.
                            </>
                          )}
                        </p>
                      </li>
                    </ul>

                    <div className='alert alert-danger'>
                      <h6 className='fw-bold'>
                        ⚠️
                        {i18n.language === 'en' ? (
                          <> Consequences of Non-Compliance</>
                        ) : (
                          <> Conséquences en cas de non-respect</>
                        )}
                      </h6>

                      <ul className='mb-0 ps-3 fs-6 fw-bold'>
                        <li>
                          {i18n.language === 'en' ? (
                            <>
                              The examination may be paused or interrupted if monitoring
                              requirements are not respected.
                            </>
                          ) : (
                            <>
                              L’examen peut être mis en pause ou interrompu si les exigences de
                              surveillance ne sont pas respectées.
                            </>
                          )}
                        </li>
                        <li>
                          {i18n.language === 'en' ? (
                            <>
                              Any suspected breach may lead to a review of the examination session.
                            </>
                          ) : (
                            <>
                              Tout manquement suspecté peut entraîner une vérification de la session
                              d’examen.
                            </>
                          )}
                        </li>
                        <li>
                          {i18n.language === 'en' ? (
                            <>
                              Confirmed misconduct may result in academic or disciplinary measures
                              in accordance with the institution’s regulations.
                            </>
                          ) : (
                            <>
                              Toute fraude confirmée peut entraîner des mesures académiques ou
                              disciplinaires conformément aux règlements de l’institution.
                            </>
                          )}
                        </li>
                        <li>
                          {i18n.language === 'en' ? (
                            <>
                              In the event of a technical issue, the student must immediately
                              contact support or the invigilator.
                            </>
                          ) : (
                            <>
                              En cas de problème technique, l’étudiant·e doit contacter
                              immédiatement le support ou le surveillant.
                            </>
                          )}
                        </li>
                      </ul>
                    </div>
                    <div
                      className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-4 rounded-4 p-5'
                      style={{
                        background: '#f7fff9',
                        border: '1px solid #d9f6df',
                      }}
                    >
                      {/* START BUTTON */}
                      <div className='d-flex gap-3 flex-wrap'>
                        <Link
                          to={`/student/entrance-exam/${encryptId(xmId)}`}
                          className='btn btn-success btn-sm fw-bolder rounded-3 start-exam-btn'
                          style={{
                            minWidth: '210px',
                            boxShadow: '0 8px 18px rgba(25,135,84,.22)',
                          }}
                        >
                          <span className='fa fa-play-circle mx-2 fs-3 play-icon'></span>
                          {t('ONLINE.ONLINE_XM')}
                        </Link>

                        <button
                          type='button'
                          className='btn btn-primary btn-sm fw-bolder rounded-3'
                          onClick={handleCampusExamClick}
                          style={{
                            minWidth: '210px',
                            boxShadow: '0 8px 18px rgba(13,110,253,.20)',
                          }}
                        >
                          <span className='fa fa-university mx-2 fs-3'></span>
                          {t('ONLINE.XM_ON_CAMPUS')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          }
          if (isFuture) {
            return (
              <div className='mb-4 mx-3'>
                <div
                  className='position-relative overflow-hidden rounded-4'
                  style={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%)',
                    boxShadow: '0 14px 40px rgba(37, 99, 235, 0.20)',
                  }}
                >
                  {/* Decorative Background Circle */}
                  <div
                    className='position-absolute rounded-circle'
                    style={{
                      width: '220px',
                      height: '220px',
                      background: 'rgba(255,255,255,0.07)',
                      top: '-120px',
                      right: '-50px',
                    }}
                  />

                  {/* Decorative Background Circle */}
                  <div
                    className='position-absolute rounded-circle'
                    style={{
                      width: '130px',
                      height: '130px',
                      background: 'rgba(255,255,255,0.05)',
                      bottom: '-70px',
                      left: '35%',
                    }}
                  />

                  {/* Decorative Glow */}
                  <div
                    className='position-absolute rounded-circle'
                    style={{
                      width: '90px',
                      height: '90px',
                      background: 'rgba(96,165,250,0.22)',
                      filter: 'blur(12px)',
                      top: '25%',
                      left: '-30px',
                    }}
                  />

                  <div className='position-relative p-6 p-md-8'>
                    <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-6'>
                      {/* LEFT SIDE */}
                      <div className='d-flex align-items-start align-items-md-center gap-4'>
                        {/* ICON */}
                        <div
                          className='d-flex align-items-center justify-content-center rounded-4 flex-shrink-0'
                          style={{
                            width: '72px',
                            height: '72px',
                            background: 'rgba(255,255,255,0.15)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255,255,255,0.22)',
                            boxShadow: '0 8px 22px rgba(0,0,0,0.08)',
                          }}
                        >
                          <i className='fas fa-calendar-check text-white fs-1'></i>
                        </div>

                        {/* INFORMATION */}
                        <div>
                          {/* BADGE */}
                          <div className='d-flex align-items-center flex-wrap gap-2 mb-3'>
                            <span
                              className='badge rounded-pill px-4 py-2 fw-bold'
                              style={{
                                background: 'rgba(255,255,255,0.16)',
                                color: '#fff',
                                border: '1px solid rgba(255,255,255,0.22)',
                                letterSpacing: '.5px',
                              }}
                            >
                              <span
                                className='d-inline-block rounded-circle mx-2'
                                style={{
                                  width: '7px',
                                  height: '7px',
                                  background: '#93c5fd',
                                  boxShadow: '0 0 0 4px rgba(147,197,253,.18)',
                                }}
                              />

                              {i18n.language === 'fr' ? 'EXAMEN À VENIR' : 'UPCOMING EXAM'}
                            </span>
                          </div>

                          {/* TITLE */}
                          <h2 className='text-white fw-bolder mb-2'>
                            {t('ONLINE.ENTRANCE_EXAMINATION')}
                          </h2>

                          {/* DESCRIPTION */}
                          <div
                            className='fs-6 mb-4'
                            style={{
                              color: 'rgba(255,255,255,0.78)',
                              maxWidth: '580px',
                              lineHeight: '1.7',
                            }}
                          >
                            {i18n.language === 'fr'
                              ? "Votre examen d'entrée est programmé. Il sera disponible à la date indiquée."
                              : 'Your entrance examination has been scheduled. It will become available on the date shown.'}
                          </div>

                          {/* STATUS */}
                          <div className='d-flex align-items-center gap-2'>
                            <i className='fas fa-info-circle' style={{color: '#bfdbfe'}}></i>

                            <span
                              className='fw-semibold fs-7'
                              style={{color: 'rgba(255,255,255,0.82)'}}
                            >
                              {i18n.language === 'fr'
                                ? "Vous ne pouvez pas encore commencer l'examen."
                                : 'The examination cannot be started yet.'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* RIGHT SIDE - DATE */}
                      <div
                        className='rounded-4 p-5'
                        style={{
                          background: 'rgba(255,255,255,0.13)',
                          border: '1px solid rgba(255,255,255,0.22)',
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          minWidth: '220px',
                        }}
                      >
                        <div className='d-flex align-items-center justify-content-center gap-2 mb-3'>
                          <i
                            className='far fa-calendar-alt'
                            style={{
                              color: '#bfdbfe',
                              fontSize: '16px',
                            }}
                          ></i>

                          <div
                            className='fw-bold text-uppercase'
                            style={{
                              color: 'rgba(255,255,255,0.68)',
                              fontSize: '11px',
                              letterSpacing: '1px',
                            }}
                          >
                            {i18n.language === 'fr' ? "DATE D'EXAMEN" : 'EXAM DATE'}
                          </div>
                        </div>

                        <div className='text-white fw-bolder fs-2 text-center mb-2'>
                          {formattedExamDate}
                        </div>

                        <div
                          className='text-center fw-semibold fs-8'
                          style={{
                            color: '#bfdbfe',
                          }}
                        >
                          {i18n.language === 'fr'
                            ? 'Préparez-vous pour votre examen'
                            : 'Prepare for your examination'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* BOTTOM INFORMATION BAR */}
                  <div
                    className='position-relative px-6 px-md-8 py-4'
                    style={{
                      background: 'rgba(15,23,42,0.14)',
                      borderTop: '1px solid rgba(255,255,255,0.10)',
                    }}
                  >
                    <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3'>
                      <div className='d-flex align-items-center gap-3'>
                        <div
                          className='d-flex align-items-center justify-content-center rounded-circle'
                          style={{
                            width: '34px',
                            height: '34px',
                            background: 'rgba(255,255,255,0.12)',
                            minWidth: '34px',
                          }}
                        >
                          <i
                            className='fas fa-clock'
                            style={{
                              color: '#dbeafe',
                              fontSize: '13px',
                            }}
                          ></i>
                        </div>

                        <span
                          className='fs-7'
                          style={{
                            color: 'rgba(255,255,255,0.76)',
                          }}
                        >
                          {i18n.language === 'fr'
                            ? 'Revenez à cette date pour commencer votre examen.'
                            : 'Return on the examination date to begin your exam.'}
                        </span>
                      </div>

                      <span
                        className='badge rounded-pill px-4 py-2'
                        style={{
                          background: 'rgba(219,234,254,0.12)',
                          color: '#dbeafe',
                          border: '1px solid rgba(219,234,254,0.20)',
                        }}
                      >
                        <i className='fas fa-lock mx-2'></i>

                        {i18n.language === 'fr' ? 'PAS ENCORE DISPONIBLE' : 'NOT YET AVAILABLE'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          }

          return (
            <div
              className={`card border-0 shadow-sm mb-4 ${
                isFuture ? 'bg-light-primary' : 'bg-light-danger'
              }`}
            >
              <div className='card-body d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3'>
                <div className='d-flex align-items-center'>
                  <div
                    className={`d-flex align-items-center justify-content-center rounded mx-4 bg-${
                      isFuture ? 'primary' : 'danger'
                    }`}
                    style={{
                      width: '52px',
                      height: '52px',
                      minWidth: '52px',
                    }}
                  >
                    <span className='fa fa-graduation-cap text-white fs-1'></span>
                  </div>

                  <div>
                    <h3 className='fw-bold mb-1'>{t('ONLINE.ENTRANCE_EXAMINATION')}</h3>
                    {isFuture && (
                      <div className='d-flex align-items-center flex-wrap gap-2'>
                        <span className='text-muted'>
                          {i18n.language === 'fr'
                            ? 'Votre examen concours aura lieu le'
                            : 'Your Kankor examination will take place on'}
                        </span>

                        <span className='fw-bold text-primary'>{formattedExamDate}</span>
                      </div>
                    )}
                    {isPassed && (
                      <div className='d-flex align-items-center flex-wrap gap-2'>
                        <span className='fw-bold text-danger'>
                          {i18n.language === 'fr'
                            ? 'La date de votre examen du concours est passée.'
                            : 'Your Entrance examination date has passed.'}
                        </span>

                        <span className='text-muted'>({formattedExamDate})</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })()}

      <StdAccountPayment />

      {planning && planning.semesters && planning.semesters.length > 0 && (
        <div className='my-3 p-none'>
          {planning.semesters.map((sem: any, index: number) => {
            const collapseId = `semesterCollapse${index}`
            const isOpen = openSemester === index

            return (
              <div key={index} className='card my-3 shadow-sm'>
                <div
                  className='card-header bg-primary text-white d-flex justify-content-between align-items-center'
                  style={{cursor: 'pointer', minHeight: '40px'}}
                  data-bs-toggle='collapse'
                  data-bs-target={`#${collapseId}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpenSemester(isOpen ? null : index)}
                >
                  <span className='fw-bold fs-4'>
                    {t('LABEL.ACADEMIC_YEAR')}: <strong>{planning.academic_year}</strong>
                    <span className='mx-3'>
                      {t('LABEL.SEMESTER')}: <strong>{sem.semester}</strong>
                    </span>
                    <span className='mx-3'>{t('LABEL.COURSE_PLANNING')}</span>
                  </span>
                  <i
                    className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-white fw-bold`}
                    style={{
                      transition: '0.5',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  ></i>
                </div>

                <div className={`collapse ${isOpen ? 'show' : ''}`} id={collapseId}>
                  <div className='card-body'>
                    <div className='table-responsive'>
                      <table className='table table-bordered align-middle'>
                        <thead className='bg-primary'>
                          <tr>
                            <th className='text-white' style={{width: '3%'}}>
                              #
                            </th>
                            <th className='text-white' style={{width: '20%'}}>
                              {t('LABEL.COURSE')}
                            </th>
                            <th className='text-white' style={{width: '15%'}}>
                              {t('LABEL.LECTURER')}
                            </th>
                            <th className='text-white'>{t('LABEL.CLASS')}</th>
                            <th className='text-white' style={{width: '50%'}}>
                              {t('LABEL.DATES')}
                            </th>
                            <th className='text-white'>{t('LABEL.EXAM_CLASS')}</th>
                            <th className='text-white' style={{width: '12%'}}>
                              {t('LABEL.EXAM_DATE_TIME')}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {sem.courses.length > 0 ? (
                            sem.courses.map((course: any, i: number) => (
                              <tr key={course.id}>
                                <th className='text-center bg-primary text-white'>{i + 1}</th>

                                <td>
                                  {course.name}
                                  <br />
                                  <span className='text-muted'>{course.code}</span>
                                </td>

                                <td>{course.lecturer_name}</td>
                                <td>{course.class}</td>
                                <td>
                                  {course.dates
                                    .slice()
                                    .sort((a, b) => {
                                      const dateA = new Date(toSortableDate(a.date)).getTime()
                                      const dateB = new Date(toSortableDate(b.date)).getTime()
                                      return dateA - dateB
                                    })
                                    .map((date) => {
                                      const jsDate = new Date(toSortableDate(date.date))

                                      const weekday = jsDate.toLocaleDateString(
                                        i18n.language === 'en' ? 'en-US' : 'fr-FR',
                                        {
                                          weekday: 'long',
                                        }
                                      )

                                      return (
                                        <span
                                          key={date.id}
                                          className='badge bg-primary text-white m-1'
                                          style={{whiteSpace: 'pre-line'}}
                                        >
                                          {date.date}
                                          {'\n'}
                                          {weekday}
                                          {'\n'}
                                          {date.time_slot}
                                        </span>
                                      )
                                    })}
                                </td>
                                <td>{course.exam_class}</td>
                                <td>{course.exam_date}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className='text-center'>
                                {t('LABEL.NO_COURSES')}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* NEWS SECTION */}
      {news.length > 0 && (
        <div className='my-4' id='print-news-section'>
          <div className='card mb-3'>
            <div className='card-header border-0 bg-white py-4 rounded-top-4 d-flex justify-content-between align-items-center'>
              <h3 className='fw-bold text-primary m-0'>{t('LABEL.NEWS_AND_ANNOUNCEMENT')}</h3>

              <div className='d-flex align-items-center gap-3 p-none' style={{minWidth: '350px'}}>
                <button
                  className='btn btn-sm btn-outline-primary p-none'
                  onClick={() => {
                    if (openNews.length === currentItems.length) {
                      setOpenNews([])
                    } else {
                      setOpenNews(currentItems.map((item: any) => item.id))
                    }
                  }}
                >
                  <span
                    className={`mx-2 fas ${
                      openNews.length === currentItems.length ? 'fa-chevron-up' : 'fa-chevron-down'
                    }`}
                  ></span>
                  {openNews.length === currentItems.length
                    ? t('LABEL.COLLAPSE_ALL')
                    : t('LABEL.EXPAND_ALL')}
                </button>

                {/* Print Button */}
                <button
                  className='btn btn-sm btn-primary p-none'
                  onClick={() => {
                    const printContents = document.getElementById('print-news-section')?.innerHTML
                    const originalContents = document.body.innerHTML

                    if (printContents) {
                      document.body.innerHTML = printContents
                      window.print()
                      document.body.innerHTML = originalContents
                      window.location.reload()
                    }
                  }}
                >
                  <span className='fas fa-print mx-2'></span>
                  {t('BUTTONS.PRINT')}
                </button>

                {/* Records Per Page */}
                <div className='d-flex align-items-center p-none'>
                  <label className='mt-2 mx-2 fw-bold text-nowrap p-none'>
                    {t('GLOBAL.RECORD_PER_PAGE')}
                  </label>

                  <select
                    className='form-select form-select-sm p-none'
                    style={{width: '110px'}}
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {currentItems.map((item: any) => (
            <div key={item.id} className='card mb-3 shadow-sm rounded-4'>
              <div
                className={`card-header d-flex flex-column justify-content-center bg-${
                  item.priority === 'Normal' ? 'primary' : 'danger'
                }`}
                style={{minHeight: '40px', cursor: 'pointer'}}
                onClick={() => {
                  if (openNews.includes(item.id)) {
                    setOpenNews(openNews.filter((id) => id !== item.id))
                  } else {
                    setOpenNews([...openNews, item.id])
                  }
                }}
              >
                <h5 className='card-title fw-bold text-white mb-0'>{item.title}</h5>
                <small className='text-light mx-3 fw-bold mb-1'>
                  <i className='fas fa-calendar-alt mx-1'></i>
                  {formatDate(item.date)}
                </small>
              </div>

              {/* PURE REACT TOGGLE */}
              {openNews.includes(item.id) && (
                <div>
                  <div className='card-body'>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: item.content,
                      }}
                    ></p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {memberExams.map((exam: any) => (
        <div key={exam.id} className='card shadow-sm mx-3 mb-4'>
          <div className='card-body d-flex justify-content-between align-items-center'>
            <div>
              <h4 className='fw-bold mb-2'>{t('ONLINE.ENTRANCE_EXAMINATION')}</h4>

              <div className='text-muted'>
                {t('LABEL.EXAM_DATE_TIME')}: {exam.exam_date}
              </div>
            </div>

            <Link
              to={`/student/entrance-exam/${encryptId(exam.id)}`}
              className='btn btn-success btn-sm fw-bold'
            >
              <span className='fa fa-play-circle mx-2'></span>
              {t('ONLINE.START_EXAM')}
            </Link>
          </div>
        </div>
      ))}
      <SocialMedia />

      {isShown && <EmergencyContact modelType={'Student'} />}
      {isActive && <InactiveUser modelType={'Student'} />}

      {isShownDoc && (
        <Modal
          isShown={isShownDoc}
          hide={toggleDoc}
          modalContent={
            <StudentDocumentCreate
              onSetShown={setIsShownDoc}
              student_document_id={0}
              id={studentId}
              reloadData={() => listData()}
            />
          }
          headerText={t('LABEL.UPLOAD_ID')}
        />
      )}
    </>
  )
}

export default StudentDashboard
